import translationsBlueprint from 'locales/models/pl.json';
import { Moment } from 'moment';
import moment from 'moment-timezone';

import ApiBundleUser from 'types/api/BundleUser';
import { Genders } from 'types/genders';
import { Locales } from 'types/locale';
import { Option } from 'types/options';

import AuthEvent from 'models/AuthEvent';
import Bundle, { genderOptions } from 'models/Bundle';
import BundlePricing from 'models/BundlePricing';
import { Timezone } from 'models/Market';
import Model from 'models/Model';
import ProductCategory from 'models/ProductCategory';
import ProductLocale from 'models/ProductLocale';

import { getFormattedDate } from 'utils/date';
import { select as selectLocale } from 'utils/locale';

export enum Statuses {
    Active = 'active',
    Inactive = 'inactive',
}

const translationsModels: typeof translationsBlueprint.comp = selectLocale({
    [Locales.En]: require('locales/models/en.json'),
    [Locales.Pl]: require('locales/models/pl.json'),
});

export default class BundleUser implements Model {
    id: string;
    modelType: string;
    createdAt: Moment;
    createdAtDisplay: string;
    name: string;
    category: ProductCategory;
    categoryOption: Option<ProductCategory>;
    status: Statuses;
    statusOption: Option<Statuses | string>;
    gender: Genders;
    genderOption: Option<Genders | string>;
    eventLimit: number;
    minProductSelection: number;
    packageBonusQuantity: number;
    packageQuantityTotal: number;
    validityDueTo: Moment;
    validityDueToDisplay: string;
    locale?: ProductLocale;
    pricing?: BundlePricing;
    bundle?: Bundle;
    events: AuthEvent[];

    constructor(data: ApiBundleUser) {
        this.id = data.id;
        this.modelType = data.modelType;
        this.createdAt = data.createdAt && moment.utc(data.createdAt).tz(Timezone);
        this.createdAtDisplay = this.createdAt && getFormattedDate(this.createdAt);
        this.name = data.name;
        this.gender = data.gender;
        this.genderOption = this.getGenderOption(this.gender);
        this.category = data.category && new ProductCategory(data.category);
        this.categoryOption = this.category && this.category?.getOption();
        this.status = data.status;
        this.statusOption = this.getStatusOption(this.status);
        this.eventLimit = data.eventLimit;
        this.minProductSelection = data.minProductSelection;
        this.packageBonusQuantity = data.packageBonusQuantity;
        this.packageQuantityTotal = data.packageQuantityTotal;
        this.validityDueTo = data.validityDueTo && moment.utc(data.validityDueTo).tz(Timezone);
        this.validityDueToDisplay = this.validityDueTo && getFormattedDate(this.validityDueTo, 'date');
        //TODO: make separate models for locale?
        this.locale = data.locale && new ProductLocale(data.locale) || null;
        this.pricing = data.pricing && new BundlePricing(data.pricing) || null;
        this.bundle = data.bundle && new Bundle(data.bundle) || null;
        this.events = Array.isArray(data.events)
            ? data.events.map(event => new AuthEvent(event))
            : [];
    }

    getOption = (): Option<BundleUser> => {
        return {
            value: this,
            label: this.name,
        };
    };

    getGenderOption = (value: Genders): Option<Genders | string> => {
        return genderOptions.find(option => option.value === value);
    };

    getStatusOption = (value: Statuses): Option<Statuses | string> => {
        return statusOptions.find(option => option.value === value);
    };
}

export const statusOptions = [{
    value: Statuses.Active,
    label: translationsModels?.bundleUser?.status?.active,
}, {
    value: Statuses.Inactive,
    label: translationsModels?.bundleUser?.status?.inactive,
}];